// src/pages/MusteriTakip.jsx  — giriş gerektirmez, public
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  doc, onSnapshot, collection, query, orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Calendar, CheckCircle2, Clock, Loader2, AlertTriangle } from 'lucide-react';

export default function MusteriTakip() {
  const { musteriId }          = useParams();
  const [musteri, setMusteri]  = useState(null);
  const [kayitlar, setKayitlar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata]        = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'musteriler', musteriId),
      snap => {
        if (snap.exists()) {
          setMusteri({ id: snap.id, ...snap.data() });
        } else {
          setHata(true);
        }
        setYukleniyor(false);
      },
      () => { setHata(true); setYukleniyor(false); }
    );
    return unsub;
  }, [musteriId]);

  useEffect(() => {
    const q = query(
      collection(db, 'musteriler', musteriId, 'kayitlar'),
      orderBy('tarih', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setKayitlar(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [musteriId]);

  function tarihFormat(ts) {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('tr-TR', {
      day: '2-digit', month: 'long', year: 'numeric'
    });
  }

  function saatFormat(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  }

  if (yukleniyor) return (
    <div className="min-h-screen bg-stone-950 flex items-center justify-center">
      <Loader2 className="animate-spin text-stone-600" size={32} />
    </div>
  );

  if (hata) return (
    <div className="min-h-screen bg-stone-950 flex flex-col items-center justify-center gap-3 text-center px-4">
      <AlertTriangle size={36} className="text-stone-700" />
      <p className="text-stone-500 text-sm">Bu sayfa bulunamadı veya erişim izniniz yok.</p>
    </div>
  );

  const ilerleme = musteri.ilerleme ?? 0;

  return (
    <div className="min-h-screen bg-stone-950 text-white">

      {/* ── HEADER ── */}
      <div className="bg-stone-900 border-b border-stone-800 px-6 py-5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-lg font-bold tracking-[5px] text-white">AGE</div>
            <div className="text-[9px] tracking-[2px] uppercase text-stone-600">
              İzolasyon ve Çatı Sistemleri
            </div>
          </div>
          <div className="text-xs text-stone-600 tracking-wider uppercase">Proje Takip</div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8 space-y-8">

        {/* ── MÜŞTERİ BAŞLIĞI ── */}
        <div>
          <p className="text-xs text-stone-600 tracking-widest uppercase mb-1">Müşteri</p>
          <h1 className="text-2xl font-semibold text-white">{musteri.isimSoyisim}</h1>
          {musteri.isTanimi && (
            <p className="text-stone-500 text-sm mt-1">{musteri.isTanimi}</p>
          )}
        </div>

        {/* ── İLERLEME ── */}
        <div className="bg-stone-900 border border-stone-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-stone-400">
              {ilerleme === 100
                ? <CheckCircle2 size={16} className="text-green-500" />
                : <Clock size={16} />}
              <span className="text-sm font-medium">
                {ilerleme === 100 ? 'Tamamlandı' : 'Devam Ediyor'}
              </span>
            </div>
            <span className="text-3xl font-bold text-white">{ilerleme}%</span>
          </div>

          <div className="w-full h-3 bg-stone-800 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${
                ilerleme === 100 ? 'bg-green-500' : 'bg-white'
              }`}
              style={{ width: `${ilerleme}%` }}
            />
          </div>

          {/* Kilometre taşları */}
          <div className="flex justify-between mt-2">
            {[0, 25, 50, 75, 100].map(n => (
              <div key={n} className="flex flex-col items-center">
                <div className={`w-1 h-1 rounded-full mt-1 ${
                  ilerleme >= n ? 'bg-white' : 'bg-stone-700'
                }`} />
                <span className="text-[10px] text-stone-600 mt-1">{n}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* ── ZİYARETÇİ NOTU ── */}
        {musteri.notlar && (
          <div className="bg-stone-900 border border-stone-800 rounded-xl p-5">
            <p className="text-xs text-stone-600 tracking-widest uppercase mb-2">Notlar</p>
            <p className="text-stone-300 text-sm leading-relaxed">{musteri.notlar}</p>
          </div>
        )}

        {/* ── TİMELINE ── */}
        <div>
          <h2 className="text-xs text-stone-600 tracking-widest uppercase mb-6">
            İş Kayıtları ({kayitlar.length})
          </h2>

          {kayitlar.length === 0 && (
            <div className="text-center text-stone-700 text-sm py-10 bg-stone-900 border border-stone-800 rounded-xl">
              Henüz kayıt eklenmedi
            </div>
          )}

          <div className="relative">
            {/* Dikey çizgi */}
            {kayitlar.length > 0 && (
              <div className="absolute left-[7px] top-2 bottom-0 w-px bg-stone-800" />
            )}

            <div className="space-y-6">
              {kayitlar.map((kayit, i) => (
                <div key={kayit.id} className="flex gap-5 relative">
                  {/* Nokta */}
                  <div className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 mt-1 z-10 ${
                    i === 0
                      ? 'border-white bg-white'
                      : 'border-stone-600 bg-stone-950'
                  }`} />

                  {/* Kart */}
                  <div className="flex-1 bg-stone-900 border border-stone-800 rounded-xl p-4 pb-5">
                    <div className="flex items-center gap-2 text-xs text-stone-600 mb-3">
                      <Calendar size={11} />
                      <span>{tarihFormat(kayit.tarih)}</span>
                      <span className="text-stone-700">·</span>
                      <span>{saatFormat(kayit.tarih)}</span>
                    </div>

                    {kayit.metin && (
                      <p className="text-stone-200 text-sm leading-relaxed mb-3">
                        {kayit.metin}
                      </p>
                    )}

                    {kayit.resimUrl && (
                      <img
                        src={kayit.resimUrl}
                        alt="Kayıt görseli"
                        className="rounded-lg w-full max-h-80 object-cover border border-stone-800 cursor-pointer hover:opacity-90 transition-opacity"
                        onClick={() => window.open(kayit.resimUrl, '_blank')}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-6 border-t border-stone-900">
          <div className="text-xs text-stone-700">
            © AGE İzolasyon ve Çatı Sistemleri
          </div>
          <a href="https://ageizolasyon.com" className="text-xs text-stone-600 hover:text-stone-400 transition-colors">
            ageizolasyon.com
          </a>
        </div>
      </div>
    </div>
  );
}
